// @ts-strict-ignore
import ProductsSlider from '@components/product-slider'
import {
  Article,
  ArticlesFilterInput,
  useArticlesByIdQuery,
  AttributeFilterFiltersInput,
} from '@generated'
import { useEmarsysStore } from '@lib/use-emarsys-store'
import { useTranslation } from 'next-i18next'
import { FC, useEffect, useState } from 'react'

type Props = {
  article: Article
}

// Horloges
// 1. Zelfde "Gender"
// 2. Zelfde "Collection_serie" (max 2)
// 3. Zelfde prijsklasse (max €200,- verschil bijvoorbeeld)
// 4. Zelfde "Dolor Dial"
// 5. Zelfde "Material Band"
// Juwelen
// Vergelijkbare juwelen parameters; in de juiste volgorde:
// 1. Zelfde "Type Jewel"
// 2. Zelfde "Collection_serie" (max 2)
// 3. Zelfde prijsklasse (max €200,- verschil bijvoorbeeld)
// 4. Zelfde "Material"

const RelatedProducts: FC<Props> = ({ article }) => {
  const [articles, setArticles] = useState<Article[]>([])
  const [attributeFilters, setAttributeFilters] = useState<AttributeFilterFiltersInput[]>([])
  const { relatedProducts } = useEmarsysStore()
  const { t } = useTranslation('other')
  let filters: ArticlesFilterInput = {
    webID: article.webID,
    attributeFilters,
    collections: [],
  }
  const [{ fetching, data }] = useArticlesByIdQuery({
    variables: { ids: relatedProducts },
    pause: !relatedProducts,
  })

  let initialFilters: AttributeFilterFiltersInput[] = []

  useEffect(() => {
    if (article.articleType === 'Horloge') {
      const gender = article.articleAttributes.find((i) => i.attributeOriginalName === 'Gender')
      if (gender) {
        initialFilters.push({
          attributeID: gender.attributeID,
          attributeValueID: [gender.attributeValueID],
        })
      }

      const pricing = article.articleAttributes.find(
        (i) => i.attributeOriginalName === 'priceCategory',
      )
      if (pricing) {
        initialFilters.push({
          attributeID: pricing.attributeID,
          attributeValueID: [pricing.attributeValueID],
        })
      }

      const dial = article.articleAttributes.find((i) => i.attributeOriginalName === 'Color Dial')
      if (dial) {
        initialFilters.push({
          attributeID: dial.attributeID,
          attributeValueID: [dial.attributeValueID],
        })
      }

      const strap = article.articleAttributes.find(
        (i) => i.attributeOriginalName === 'Material Strap',
      )
      if (strap) {
        initialFilters.push({
          attributeID: strap.attributeID,
          attributeValueID: [strap.attributeValueID],
        })
      }

      if (article.collectionNumber) {
        filters.collections = [article.collectionNumber]
      }
    } else if (article.articleType === 'Juweel') {
      filters.collections = [2]

      const pricing = article.articleAttributes.find(
        (i) => i.attributeOriginalName === 'priceCategory',
      )
      if (pricing) {
        initialFilters.push({
          attributeID: pricing.attributeID,
          attributeValueID: [pricing.attributeValueID],
        })
      }

      const material = article.articleAttributes.find(
        (i) => i.attributeOriginalName === 'Material Strap',
      )
      if (material) {
        initialFilters.push({
          attributeID: material.attributeID,
          attributeValueID: [material.attributeValueID],
        })
      }

      if (article.collectionNumber) {
        filters.collections = [article.collectionNumber]
      }
    }

    setAttributeFilters(initialFilters)
  }, []) // eslint-disable-line

  useEffect(() => {
    if (data) {
      // If we have no results and have more than one attributeFilter applied, subtract an attributeFilter to retrigger fetch
      // OR
      // If we do have results, but the array only consists of the current product, and we have more than one attributeFilter applied, subtract an attributeFilter to retrigger fetch
      if (
        (data.articlesById.length === 0 && attributeFilters.length > 1) ||
        (data.articlesById.filter((a) => a.id !== article.id).length === 0 &&
          attributeFilters.length > 1)
      ) {
        const filterMinusOne = attributeFilters.slice(0, attributeFilters.length - 1)
        setAttributeFilters(filterMinusOne)
      }
      setArticles(data?.articlesById)
    }
  }, [data]) // eslint-disable-line

  return (
    <ProductsSlider
      emarsysContainerId="related-products"
      isLoading={fetching || !relatedProducts}
      articles={articles.filter((a) => a.id !== article.id)}
      title={t('relatedProducts')}
    />
  )
}

export default RelatedProducts
